import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Joi from 'joi';

import { useUpdateBankingDataMutation } from 'api/main';
import {
    bicValidationSchema,
    checkAcceptedFileTypes,
    formatIBANOnType,
    validateFileSize,
    validateSchema,
} from 'utils/helpers';
import { bicValidationMessages } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    CancelButton,
    CheckboxLabel,
    CustomDialogTitle,
    CustomTextField,
    ErrorMessage,
    FileErrorMessage,
    FileInputButton,
    InfoButton,
    SuccessMessage,
} from 'components';
import { Box, Dialog, DialogActions, DialogContent, FormLabel, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { userSelectors } from 'reducers';
import { subscriptionStatus } from 'utils/constants';

const schema = {
    rib: Joi.any().invalid(null).messages({ 'any.invalid': texts.PROVIDE_NEW_RIB }),
    bic: Joi.string().custom(bicValidationSchema).messages(bicValidationMessages),
    iban: Joi.string().required().messages({
        'string.empty': texts.SAISIR_IBAN,
        'any.required': texts.SAISIR_IBAN,
    }),
};

const RibReplaceDialog = ({ onClose, open, doc, bankingStatement }) => {
    const internalUser = useSelector(userSelectors.getInternalUser);
    const [form, setForm] = useState({
        iban: '',
        rib: null,
        bic: '',
        updateRibForAllBankingStatements: false,
    });
    const [errors, setErrors] = useState({ iban: '', rib: '', bic: '' });
    const [replaceError, setReplaceError] = useState('');
    const [fileTypeError, setFileTypeError] = useState(false);
    const [fileSizeError, setFileSizeError] = useState(false);

    const [updateBankingData, { isLoading, isSuccess, error, isError, reset }] =
        useUpdateBankingDataMutation();

    const finishedSubscriptions = useMemo(
        () =>
            internalUser.subscriptions.filter(
                s => s.subscriptionStatus === subscriptionStatus.FINISHED
            ),
        [internalUser.subscriptions]
    );
    const handleFileChange = useCallback(({ target: { files } }) => {
        const file = files[0];
        checkAcceptedFileTypes(
            file,
            () => {
                if (validateFileSize(file)) {
                    setFileTypeError(false);
                    setForm(prevState => ({ ...prevState, rib: file }));
                    setFileSizeError(false);
                } else {
                    setFileSizeError(true);
                }
            },
            () => {
                setForm(prevState => ({ ...prevState, rib: null }));
                setFileTypeError(true);
                setFileSizeError(false);
            }
        );
    }, []);
    const handleIBANChange = useCallback(e => {
        setForm(prevState => ({ ...prevState, iban: formatIBANOnType(e.target) }));
        setErrors({ iban: '', rib: '', bic: '' });
    }, []);
    const handleBICChange = useCallback(({ target }) => {
        setForm(prevState => ({
            ...prevState,
            bic: target.value ? target.value.toUpperCase().replace(/\s/g, '') : '',
        }));
    }, []);
    const handleCheckboxChange = useCallback(e => {
        const checked = e.target.checked;
        setForm(prevState => ({ ...prevState, updateRibForAllBankingStatements: checked }));
    }, []);

    const bankAccountIsTheSame = useMemo(
        () =>
            form.iban &&
            bankingStatement?.bankAccountNumber?.replace(/\s/g, '') ===
                form.iban.replace(/\s/g, ''),
        [bankingStatement, form.iban]
    );

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        if (valid) {
            setErrors({ iban: '', bic: '', rib: '' });
            setReplaceError('');
            updateBankingData({ ...form, id: bankingStatement?.id, ribId: doc.id, bic: form.bic });
        } else {
            setErrors(newErrors);
        }
    }, [form, updateBankingData, bankingStatement, doc]);

    const handleClose = useCallback(() => {
        onClose();
        setForm({ iban: '', rib: null, bic: '' });
        setReplaceError('');
        setErrors({ iban: '', rib: '', bic: '' });
    }, [onClose]);

    useEffect(() => {
        if (isSuccess && !bankAccountIsTheSame) {
            reset();
            handleClose();
        }
    }, [handleClose, isSuccess, reset, bankAccountIsTheSame]);

    useEffect(() => {
        if (isError) {
            if (error?.status === 400 || error?.status === 422) {
                setReplaceError(texts.IBAN_REPLACE_PROBLEM);
            } else if (error?.status === 415) {
                setFileSizeError(true);
            }
            reset();
        }
    }, [isError, error, reset]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={texts.RIB_REPLACEMENT} handleClose={handleClose} />
            <DialogContent>
                <Box mt="20px" mb={2}>
                    <FormLabel
                        sx={{ marginBottom: '8px', letterSpacing: '1.6px' }}
                        component="legend"
                    >
                        {texts.PROVIDE_NEW_RIB}
                    </FormLabel>
                    <Typography
                        sx={{
                            marginRight: 2,
                            textAlign: 'left',
                            marginBottom: '5px',
                            marginTop: 2,
                            maxWidth: '550px',
                        }}
                        variant="body2"
                    >{`${texts.UPLOADED_FILE_NAME} : ${form.rib?.name || texts.AUCUN}`}</Typography>
                    {fileTypeError && <FileErrorMessage variant="type" />}
                    {fileSizeError && <FileErrorMessage variant="size" />}
                    <FileInputButton
                        noMargin
                        showIcon
                        id="rib-doc"
                        label={texts.UPLOAD_FILE_LABEL}
                        onFileChange={handleFileChange}
                    />
                    <ErrorMessage message={errors.rib} />
                </Box>
                <CustomTextField
                    label={texts.SUBMIT_NEW_IBAN}
                    onChange={handleIBANChange}
                    value={form.iban}
                    error={errors.iban}
                    showError
                    inputProps={{ maxLength: 42 }}
                />
                <CustomTextField
                    fullWidth
                    label={texts.BIC_ALT_LABEL}
                    onChange={handleBICChange}
                    value={form.bic}
                    error={errors.bic}
                    inputProps={{ maxLength: 11 }}
                    infoButton={
                        <InfoButton
                            disableMarginBottom
                            title={texts.BIC_FORMAT_TITLE}
                            content={texts.BIC_FORMAT_CONTENT}
                        />
                    }
                />
                {finishedSubscriptions?.length >= 2 && (
                    <CheckboxLabel
                        label={texts.REPLACE_RIB_FOR_ALL_SUBSCRIPTIONS}
                        checked={form.updateRibForAllBankingStatements}
                        onChange={handleCheckboxChange}
                    />
                )}
                <ErrorMessage message={replaceError} />
                <SuccessMessage
                    align="left"
                    message={isSuccess && bankAccountIsTheSame ? texts.SAME_IBAN_NEW_RIB : ''}
                />
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={isLoading} onClick={handleClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton
                    disabled={isLoading || isSuccess}
                    onClick={handleSubmit}
                    loading={isLoading}
                >
                    {texts.SAVE}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

RibReplaceDialog.propTypes = {
    onClose: PropTypes.func,
    open: PropTypes.bool,
    doc: PropTypes.object,
    bankingStatement: PropTypes.object,
};

export default RibReplaceDialog;
